/**
 * Which Terraform variables are secrets, and how to carry them separately.
 *
 * A run's variables are supplied by the caller and forwarded to the installer in
 * someone else's account. Three of them are secrets — `registry_key`,
 * `openai_api_key` and `globalization.google_client_private_key` — and the
 * transport in between retains what it carries: a Step Functions execution's
 * input sits in its history for 90 days, readable by anyone holding
 * `states:GetExecutionHistory`.
 *
 * That is the same reasoning that keeps the external ID out of the execution
 * input, so these take the same route: the secret half is encrypted with KMS,
 * stored for the life of the run, and read back by the dispatch worker. What
 * travels through the execution is the redacted half.
 *
 * **The declared list is not the only defence.** `TerraformVariables` carries an
 * index signature so a new chart can add a variable without a release here,
 * which means a future secret can arrive under a name this file has never heard
 * of. `isSecretVariableName` therefore matches on the *shape* of the name, and
 * over-matches on purpose: routing a public key down the encrypted path costs a
 * few hundred bytes of ciphertext, while missing a private one writes it into
 * history for 90 days. Same trade as the log redactor's `high-entropy` rule.
 *
 * Note the normalization strips `_`, so `api_key` and `apiKey` both reduce to
 * `apikey`. Matching fragments against the raw name with word boundaries would
 * miss every name the installer actually uses — `\b` does not fire around `_`.
 *
 * **These rules describe a variables document, not the envelope around one.**
 * The `key` suffix matches `accountKey`, which is an identifier the control
 * plane puts beside the variables in an execution input — harmless there, but it
 * means `findSecretShapedPaths` belongs on `input.variables` rather than on a
 * whole request. Narrowing the rule to spare that one name would cost more than
 * it buys: the suffix is what catches `registry_key` and any `*_key` a future
 * chart introduces.
 */
/**
 * The secrets the config contract names, as paths from the variables root.
 *
 * Redundant with `isSecretVariableName` today — every one of them also matches
 * the heuristic — and kept anyway, because this is the contract stating which of
 * its own fields are secret rather than a pattern inferring it. If a later
 * rename made one of these stop matching the heuristic, this list still catches
 * it.
 */
export const SECRET_VARIABLE_PATHS = [
    ['registry_key'],
    ['openai_api_key'],
    ['globalization', 'google_client_private_key'],
];
/**
 * Name fragments that mark a value as secret, matched after normalization.
 *
 * `key` is handled separately below: as a fragment it would match
 * `google_document_sheet_title`'s neighbours and anything merely *about* a key,
 * so it only counts as a suffix.
 */
const SECRET_NAME_FRAGMENTS = [
    'password',
    'passwd',
    'passphrase',
    'secret',
    'token',
    'credential',
    'apikey',
    'privatekey',
];
const normalize = (name) => name.toLowerCase().replace(/[^a-z0-9]/g, '');
/**
 * Whether a variable name looks like it holds a secret.
 *
 * Deliberately generous. The cost of a false positive is that a public value
 * travels encrypted; the cost of a false negative is a credential in an
 * execution history nobody thinks to look at.
 */
export const isSecretVariableName = (name) => {
    const normalized = normalize(name);
    if (SECRET_NAME_FRAGMENTS.some((fragment) => normalized.includes(fragment)))
        return true;
    // Suffix rather than substring: `registry_key` and `openai_api_key` end in it,
    // while `keyspace` or `keep_alive` do not mean a secret.
    return normalized.endsWith('key');
};
const isPlainObject = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
const samePath = (a, b) => a.length === b.length && a.every((segment, index) => segment === b[index]);
const isDeclaredSecret = (path) => SECRET_VARIABLE_PATHS.some((declared) => samePath(declared, path));
/**
 * Splits a variables document into what may be remembered and what may not.
 *
 * Arrays keep their length: only object *keys* are ever removed, because an
 * array element has no name and so cannot be recognised as a secret. Removing
 * one would shift every index after it, and the installer reads these by
 * position.
 */
export const splitSecretVariables = (variables) => {
    const secrets = [];
    const walk = (value, path) => {
        if (Array.isArray(value)) {
            return value.map((item, index) => walk(item, [...path, index]));
        }
        if (isPlainObject(value)) {
            const kept = {};
            for (const [key, child] of Object.entries(value)) {
                const childPath = [...path, key];
                if (isDeclaredSecret(childPath) || isSecretVariableName(key)) {
                    secrets.push({ path: childPath, value: child });
                    continue;
                }
                kept[key] = walk(child, childPath);
            }
            return kept;
        }
        return value;
    };
    return {
        redacted: walk(variables, []),
        secrets,
    };
};
/** Deep copy via JSON, which is total over `JsonValue` by construction. */
const clone = (value) => JSON.parse(JSON.stringify(value));
/**
 * Puts the secrets back where they came from.
 *
 * The inverse of `splitSecretVariables`, so what the installer receives is
 * byte-identical to what the caller sent — the contract's output shape is not
 * ours to alter, and a variable arriving in a different place is a deployment
 * that fails deep in an apply.
 */
export const mergeSecretVariables = (redacted, secrets) => {
    const result = clone(redacted);
    for (const { path, value } of secrets) {
        if (path.length === 0)
            continue;
        let cursor = result;
        for (let index = 0; index < path.length - 1; index += 1) {
            const segment = path[index];
            const existing = cursor[segment];
            if (typeof existing !== 'object' || existing === null) {
                // The container survives redaction — only leaves are removed — so this
                // is for a hand-built pair rather than a split we produced.
                cursor[segment] = typeof path[index + 1] === 'number' ? [] : {};
            }
            cursor = cursor[segment];
        }
        cursor[path[path.length - 1]] = clone(value);
    }
    return result;
};
/**
 * Every secret-shaped location in an arbitrary document.
 *
 * The assertion side of the same rules: a test walks whatever is about to be
 * handed to Step Functions and requires this to come back empty, so adding a
 * variable cannot quietly reintroduce the problem this module exists to solve.
 */
export const findSecretShapedPaths = (value, path = []) => {
    if (Array.isArray(value)) {
        return value.flatMap((item, index) => findSecretShapedPaths(item, [...path, index]));
    }
    if (typeof value === 'object' && value !== null) {
        return Object.entries(value).flatMap(([key, child]) => {
            const childPath = [...path, key];
            return isDeclaredSecret(childPath) || isSecretVariableName(key)
                ? [childPath]
                : findSecretShapedPaths(child, childPath);
        });
    }
    return [];
};
//# sourceMappingURL=secrets.js.map